/**
 * This is a custom Dijit and is the base-class of the custom HorizontalSlider and VerticalSlider.
 * This class is not meant to be new-ed.
 * In terms of features, it provides a Slider which consists of a Track and Thumb.
 * The user may drag the thumb to put it in a different position on the track or click on the track to make the thumb move to that position.
 * Additionally, the user can set minor-ticks, major-ticks and major-tick-labels, which are also accesible for user interactions.
 * But limitation here is, even though the user clicks on a major-tick-label for eg. 75 the thumb will not snap to the exact value.
 *
 */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom-geometry",
    "dojo/_base/event",
    "dijit/focus",
    "dijit/form/_FormValueWidget",//subclasses  both _FormWidget(which subclasses _WidgetBase, _TemplateMixin, _CssStateMixin)
    "dijit/_Container",
    "dojo/text!./templates/Slider.html",

    //Dojo dnd
    "dojo/dnd/Moveable",
    "./_SliderThumbMover"

],
    function (declare, lang, domGeometry, event, focus, _FormValueWidget, _Container, template, Moveable,
              _SliderThumbMover) {

        return declare([_FormValueWidget, _Container], {

            templateString: template,

            //attach-point to the bar portion of the slider
            sliderTrackNode: null,

            //attach-point to the thumb of the Slider
            sliderThumbNode: null,

            value: 0,
            maximum: 100,
            minimum: 0,

            THUMB_WIDTH: 14,

            // The dimensions which represent exactly the Slider bounds with its outer-art i.e. ticks and tick-labels
            // the defaults below are not not accurate. these values are updated only after a call to computePreferredSize
            preferredSize: {
                width: 0,
                height: 0,
                left: 0,
                bottom: 0
            },

            _setHeightAttr: function (height) {
                this.sliderTrackNode.style.height = height + "px";
                this._set("height", height);
            },

            _setOuterWidthAttr: function (outerWidth) {
                this.domNode.style.width = outerWidth + "px";
            },

            _setOuterHeightAttr: function (outerHeight) {
                this.domNode.height = outerHeight + "px";
            },

            _setLocationAttr: function (location) {
                this._set("location", location);
            },

            /**
             *
             * @param pixelValue -Number - position of the pixel-point on the sliderTrack
             * @param maxPixels - Number - width/height of the Horizontal/Vertical Slider
             * @param priorityChange - Boolean - should this trigger "change" event
             * @private
             */
            _setPixelValue: function (pixelValue, maxPixels, priorityChange) {
                if (this.disabled || this.readOnly) {
                    return;
                }
                this._setValueAttr(Math.max(Math.min((this.maximum - this.minimum) * pixelValue / maxPixels + this.minimum,
                    this.maximum), this.minimum), priorityChange);
            },

            _setValueAttr: function (value, priorityChange) {
                this._set("value", value);
                this.valueNode.value = value;
                this.inherited(arguments); //to call _FormValueMixin's _handleOnChange()
            },

            _setMinimumAttr: function (minimum) {
                this._set("minimum", minimum);
            },

            _setMaximumAttr: function (maximum) {
                this._set("maximum", maximum);
            },

            /**
             * Creates a major-tick set using the custom Rule Dijit . Major-ticks are taller in length compared to the minor-ticks and over lap them if both of them are present
             * @param majorTicks - Array of Numbers
             * @private
             */
            _setMajorTicksAttr: function (majorTicks) {
                if (this.majorTickDijit) {
                    this.majorTickDijit.destroy();
                    this.majorTickDijit = null;
                    if (majorTicks.length === 0) {
                        this._set("majorTicks", majorTicks);
                        return;
                    }
                }
                var calibratedTickObj = this.constrainTicksToScaleLimits(majorTicks,
                    [this.get("minimum"), this.get("maximum")]), Rule = this.getRuleConstructor();  //majorTicks.length;
                if (calibratedTickObj.ticks.length === 0) {
                    this._set("majorTicks", majorTicks);
                    return;
                }
                this._set("majorTicks", calibratedTickObj.ticks.slice(0));
                this.majorTickDijit = new Rule({
                    "class": "vc-MajorTickMarks",
                    ticks: this.majorTicks,
                    minimum: this.minimum,
                    maximum: this.maximum,
                    count: this.majorTicks.length
                });
                this.majorTickDijit.placeAt(this.containerNode);
            },

            /**
             * Creates  minor-tick set using the custom Rule Dijit. Minor-ticks are shorter in length compared to mino-ticks and fall below major-ticks if both are present
             * @param minorTicks
             * @private
             */
            _setMinorTicksAttr: function (minorTicks) {
                if (this.minorTickDijit) {
                    this.minorTickDijit.destroy();
                    this.minorTickDijit = null;
                    if (minorTicks.length === 0) {
                        this._set("minorTicks", minorTicks);
                        return;
                    }
                }
                var calibratedTickObj = this.constrainTicksToScaleLimits(minorTicks,
                    [this.get("minimum"), this.get("maximum")]), Rule = this.getRuleConstructor();  //majorTicks.length;
                //if no ticks need to be drawn return
                if (calibratedTickObj.ticks.length === 0) {
                    this._set("minorTicks", minorTicks);
                    return;
                }
                this._set("minorTicks", calibratedTickObj.ticks.slice(0));

                this.minorTickDijit = new Rule({
                    "class": "vc-MinorTickMarks",
                    count: this.minorTicks.length,
                    ticks: this.minorTicks,
                    minimum: this.minimum,
                    maximum: this.maximum
                });
                this.minorTickDijit.placeAt(this.containerNode);
            },

            /**
             * Creates labels under the ticks corresponding to the major-ticks, using the custom RuleLabel Dijit.
             * These cannot be created without major-ticks
             * @param majorTickLabels
             * @private
             */
            _setMajorTickLabelsAttr: function (majorTickLabels) {
                if (this.majorTickLabelDijit) {
                    this.majorTickLabelDijit.destroy();
                    this.majorTickLabelDijit = null;
                }
                //Reasoning on why the following "if" statement needs to be outside of the previous "if":
                // The major-tick label Dijit has been previously removed AND now an empty "majorTickLabels" array is passed, then it results in creation of a major-tick label Dijit with NO labels
                if (majorTickLabels.length === 0 || majorTickLabels.join('').trim().length === 0) {//join the array delimited by empty string and trim the spaces
                    //the 2nd check is a workaround: Matlab is sending an array of N empty strings where it could send an empty array
                    this._set("majorTickLabels", majorTickLabels);
                    return;
                }

                var calibratedTickLabelObj = this.constrainTicksToScaleLimits(this.get("majorTicks"),
                    [this.get("minimum"), this.get("maximum")], majorTickLabels), RuleLabels = this.getRuleLabelConstructor();
                if (calibratedTickLabelObj.labels.length === 0) {
                    this._set("majorTickLabels", majorTickLabels);
                    return;
                }

                this._set("majorTickLabels", calibratedTickLabelObj.labels.slice(0));
                this.majorTickLabelDijit = new RuleLabels({
                    "class": "vc-MajorTickLabels",
                    minimum: this.minimum,
                    maximum: this.maximum,
                    labels: this.majorTickLabels,
                    count: this.majorTickLabels.length,
                    ticks: this.majorTicks
                });
                this.majorTickLabelDijit.placeAt(this.containerNode);
            },

            /**
             * Handler to move the thumb when clicked on the track.
             * The handler has been wired in the template using the data-dojo-attach-event "press" which works consistently for both mouse and touch devices
             */
            _onTrackClick: function (e) {
                if (this.disabled || this.readOnly || e.target === this.sliderThumbNode) {
                    return;
                }
                focus.focus(this.sliderThumbNode);
                event.stop(e);

                //pixel value of the mouse-click point
                var sliderTrackDimensions = domGeometry.position(this.sliderTrackNode),
                    pixelValue = e[this._mousePixelCoord] - sliderTrackDimensions[this._startingPixelCoord];

                if (this._isScaleDirectionReverse) {// true for the vertical slider
                    pixelValue = sliderTrackDimensions[this._pixelCount] - pixelValue;
                }

                this._setPixelValue(pixelValue, sliderTrackDimensions[this._pixelCount], true);

                //emit the clicked event
                this.emit("mouseclicked");

                // make the thumb stick if the user wants to continue dragging
                this._thumbMoveable.onMouseDown(e);
            },

            /**
             * Additional tasks performed here are:
             * 1. A dojo/dnd/Moveable object is created on the Slider Thumb
             * 2. A watch is set up on properties which may need the SliderThumb's position to be changed
             */
            postCreate: function () {
                this.inherited(arguments);

                //set up handlers for mousedrag and mousedragrelease using the Moveable and Mover classes
                //custom mover object for the slider
                var mover = declare(_SliderThumbMover, {
                    widget: this // pointer to the slider instance to use in the mover
                });
                //create a Moveable object on the thumb with the custom mover
                this._thumbMoveable = new Moveable(this.sliderThumbNode, {mover: mover});

                //move the thumb when value/ mininum / maximum changes
                this.own(this.watch("value", lang.hitch(this, "_adjustThumbPosition")));
                this.own(this.watch("minimum", lang.hitch(this, "_adjustThumbPosition")));
                this.own(this.watch("maximum", lang.hitch(this, "_adjustThumbPosition")));

                //an explicit call is needed because of this use-case:
                //the watches above are set up post-creation, which means the defaults for the properties have already been set
                // so the watch-callback, in this case _adjustThumbPosition, will not be called during the default setting
                this._adjustThumbPosition();
            },

            /**
             * Helper method to set the new position of the SliderThumb based on the values of the properties:
             * value, maximum, minimum
             * @private
             */
            _adjustThumbPosition: function () {
                var percent = (this.value - this.minimum) / (this.maximum - this.minimum);
                percent = percent * 100 - ((this.THUMB_WIDTH / this[this._thumbPositionParam1]) / 2) * 100;
                this.sliderThumbNode.style[this._thumbPositionParam2] = percent + "%";
            },

            /**
             * Calculates the total bounds of the Slider including its ticks and tick-labels and
             * sets the the preferredSize property
             */
            computePreferredSize: function () {
                var preferredX, preferredY, preferredWidth, preferredHeight;
                //this info is used for many calculation after this
                this.sliderThumbSize = domGeometry.position(this.sliderThumbNode);

                //Get Preferred Width
                preferredWidth = this._computePreferredWidth();

                //Get OuterX
                preferredX = this._computePreferredX();

                //Get Preferred Height
                preferredHeight = this._computePreferredHeight();

                //calculate OuterY
                preferredY = this._computePreferredY();

                this.set("preferredSize", {
                    width: preferredWidth,
                    height: preferredHeight,
                    left: preferredX,
                    bottom: preferredY
                });
            },

            /**
             * Helper method which returns the domNode of the tick/tick-label widget. This means it is the domNode:
             * 1. At the bottom-most in case of the HorizontalSlider
             * 2. At the right-most in case of the VerticalSlider
             * @private
             */
            _getDomNodeOfWidgetAtEnd: function () {
                var domNode;
                ///order-dependent if statements
                if (this.majorTickLabelDijit) {
                    domNode = this.majorTickLabelDijit.domNode;
                } else if (this.majorTickDijit) {
                    domNode = this.majorTickDijit.domNode;
                } else if (this.minorTickDijit) {
                    domNode = this.minorTickDijit.domNode;
                }
                return domNode;
            }
        });
    });